<template>
  <CustomModal
    :title="isEditMode ? 'Update Suite' : 'Create Suite'"
    width="34vw"
    v-model:visible="internalVisible"
  >
    <a-form
      ref="formRef"
      :model="formData"
      :rules="rules"
      layout="vertical"
      :id="`${source}_add_suite_section`"
      :required-mark="false"
      @finish="onFinish"
    >
      <a-form-item label="Name" name="name" class="mb-3">
        <a-input
          ref="nameInputRef"
          v-model:value="formData.name"
          class="input_medium"
          placeholder="Enter suite name"
          :autofocus="true"
        />
      </a-form-item>

      <a-form-item label="Description" name="description" class="mb-0">
        <a-textarea
          v-model:value="formData.description"
          class="input_medium"
          placeholder="Enter description"
          :rows="3"
        />
      </a-form-item>
    </a-form>

    <template #footer>
      <div class="flex flex-row items-center justify-end gap-2 w-full">
        <a-button
          class="secondary_medium"
          :disabled="creating"
          @click="handleCancel"
        >
          Cancel
        </a-button>

        <a-button
          class="primary_medium"
          html-type="submit"
          :form="`${source}_add_suite_section`"
          :loading="creating"
          :disabled="isSubmitDisabled"
        >
          {{
            creating
              ? isEditMode
                ? "Updating..."
                : "Creating..."
              : isEditMode
                ? "Update"
                : "Create"
          }}
        </a-button>
      </div>
    </template>
  </CustomModal>
</template>

<script setup>
import { computed, reactive, ref, watch, onMounted, nextTick } from "vue";
import { message } from "ant-design-vue";
import { qaEvents } from "@/src/constants/qaConstants.js";
import eventBus from "@/src/utils/eventBus.js";
import QAService from "@/src/api/QAService.js";
import CustomModal from "@/src/components/CustomModal.vue";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  space: { type: Object, default: null },
  suite: { type: Object, default: null },
  mode: { type: String, default: "create" },
});

const emit = defineEmits(["update:visible"]);

const creating = ref(false);
const internalVisible = ref(props.visible);
const formRef = ref(null);
const nameInputRef = ref(null);

const formData = reactive({
  name: "",
  description: "",
});

const rules = {
  name: [
    {
      required: true,
      message: "Please enter suite name",
      trigger: "change",
    },
  ],
};

const isEditMode = computed(() => {
  return props.mode === "edit";
});

const isSubmitDisabled = computed(() => {
  return !formData.name || formData.name.trim() === "" || creating.value;
});

const resetForm = () => {
  try {
    Object.assign(formData, {
      name: "",
      description: "",
    });
    creating.value = false;
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  } catch (error) {
    console.error("An error occurred while resetting form: ", error);
  }
};

const handleCancel = () => {
  try {
    internalVisible.value = false;
    resetForm();
  } catch (error) {
    console.error("An error occurred while closing modal: ", error);
  }
};

const onFinish = async () => {
  if (isEditMode.value) {
    await toUpdateSuite();
  } else {
    await toCreateSuite();
  }
};

const toCreateSuite = async () => {
  try {
    creating.value = true;

    const payload = {
      name: formData.name,
      description: formData.description,
    };

    const response = await QAService.createSuite(props.space?.id, payload);
    if (response?.suite) {
      message.success(`Suite ${formData.name} created successfully!`);

      const eventData = {
        event: qaEvents.CREATED,
        item: response.suite,
        spaceId: props.space?.id,
      };

      internalVisible.value = false;
      resetForm();

      eventBus.emit(qaEvents.SUITE_MANIPULATED, eventData);
    }
  } catch (error) {
    console.error("An error occurred while creating suite: ", error);
    message.error("An error occurred while creating a suite!");
  } finally {
    creating.value = false;
  }
};

const toUpdateSuite = async () => {
  try {
    creating.value = true;

    const payload = {
      name: formData.name,
      description: formData.description,
    };

    const response = await QAService.updateSuite(props.suite?.id, payload);
    if (response?.suite) {
      message.success(`Suite updated successfully!`);

      internalVisible.value = false;
      resetForm();

      eventBus.emit(qaEvents.SUITE_MANIPULATED, qaEvents.UPDATED);
    }
  } catch (error) {
    console.error("An error occurred while updating suite: ", error);
    message.error("An error occurred while updating suite!");
  } finally {
    creating.value = false;
  }
};

watch(
  () => props.visible,
  async (newVal) => {
    internalVisible.value = newVal;
    if (newVal) {
      await nextTick();
      nameInputRef.value?.focus();
    }
  },
  { immediate: true },
);

watch(
  () => internalVisible.value,
  (newVal) => {
    emit("update:visible", newVal);
    if (!newVal) {
      resetForm();
    }
  },
);

onMounted(() => {
  if (isEditMode.value && props.suite) {
    formData.name = props.suite.name || "";
    formData.description = props.suite.description || "";
    if (formRef.value) {
      formRef.value.clearValidate();
    }
  }
});

watch(
  () => [props.suite, props.visible, props.mode],
  ([suite, visible, mode]) => {
    if (visible && mode === "edit" && suite) {
      formData.name = suite.name || "";
      formData.description = suite.description || "";
      if (formRef.value) {
        formRef.value.clearValidate();
      }
    }
  },
  { immediate: true },
);
</script>
